import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PhoneIcon from '@mui/icons-material/Phone';
import { format } from 'date-fns';
import FintechExperienceIcon from '@/assets/trio-fintech-icon.png';
import { Candidate } from '@/services/candidates';
import { fullDateWithHours } from '@/utils/checkins';
import * as Styles from './CandidateResume.styles';
import CandidateProfilePictureManager from '../CandidateProfilePictureManager';
import Menu from '../Menu';
import Tooltip from '../Tooltip';

export type CandidateResumeType = Candidate;

type CandidateResumeWithUpload = CandidateResumeType & {
  refetchCandidate: () => void;
};

const BIO_LINK = 'bio.trio.dev';

type CandidateResumeProps = {
  candidate?: CandidateResumeType;
  isNotesPage?: boolean;
  openDeleteDialog: () => void;
} & Pick<CandidateResumeWithUpload, 'refetchCandidate'>;

const CandidateNameStatusAndPosition = ({
  firstName,
  lastName,
  status,
  pictureUrl,
  developerType,
  id,
  refetchCandidate,
  hasFintechExperience,
}: CandidateResumeWithUpload): JSX.Element => {
  return (
    <Styles.Info data-testid="candidate-info">
      <CandidateProfilePictureManager
        firstName={firstName}
        lastName={lastName}
        pictureUrl={pictureUrl ?? ''}
        refetchCandidate={refetchCandidate}
        pictureIsEditable
        id={id}
      />

      <Styles.InfoResume>
        <Styles.Info>
          <Styles.CandidateName data-testid="candidate-name">
            {firstName} {lastName}
          </Styles.CandidateName>
          {hasFintechExperience && (
            <Tooltip label="This person has Fintech experience" placement="top">
              <Styles.FintechExperienceIcon src={FintechExperienceIcon} alt="Fintech Experience" />
            </Tooltip>
          )}
        </Styles.Info>
        <Styles.Info>
          {status && <Styles.Tag outlined status={status} />}
          <Styles.SimpleText data-testid="candidate-developer-type">
            {developerType}
          </Styles.SimpleText>
        </Styles.Info>
      </Styles.InfoResume>
    </Styles.Info>
  );
};

const CandidateCreationDate = ({
  candidate,
  openDeleteDialog,
}: Pick<CandidateResumeProps, 'candidate' | 'openDeleteDialog'>): JSX.Element => {
  return (
    <Styles.CandidateContact>
      {candidate?.createdAt && (
        <Styles.SpaceBetween>
          <Styles.SmallerText>Created</Styles.SmallerText>
          <Styles.SimpleText data-testid="candidate-created-at">
            {format(new Date(candidate.createdAt), fullDateWithHours)}
          </Styles.SimpleText>
        </Styles.SpaceBetween>
      )}
      {candidate?.updatedAt && (
        <Styles.SpaceBetween>
          <Styles.SmallerText>Last Modified</Styles.SmallerText>
          <Styles.SimpleText data-testid="candidate-updated-at">
            {format(new Date(candidate.updatedAt), fullDateWithHours)}
          </Styles.SimpleText>
        </Styles.SpaceBetween>
      )}
      <Menu
        icon={MoreHorizIcon}
        menuId="delete-candidate-button"
        menuItems={[
          {
            label: 'Delete',
            menuItemOptions: { warning: true },
            menuItemIcon: DeleteIcon,
            callback: (e) => {
              e.stopPropagation();
              openDeleteDialog();
            },
          },
        ]}
      />
    </Styles.CandidateContact>
  );
};

const CandidateContactInfo = ({
  linkedinUrl,
  mobilePhone,
  email,
  id,
}: CandidateResumeType): JSX.Element => {
  const getValidUrl = (url: string): string => {
    if (!url) {
      return '';
    }
    if (url?.startsWith('http')) {
      return url;
    }
    return `//${url}`;
  };
  const getTrioBioUrl = (candidateId: string): string => {
    if (!candidateId) {
      return '';
    }

    return `${BIO_LINK}/${candidateId}`;
  };
  const linkedinValidLink = getValidUrl(linkedinUrl);
  const trioProfileValidLink = getValidUrl(getTrioBioUrl(id));
  const candidateBioUrl = getTrioBioUrl(id);
  const mask = '---';

  return (
    <Styles.CandidateContact fullWidth>
      <Styles.ContactInfo data-testid="candidate-email">
        <EmailIcon />
        <p>{email || mask}</p>
      </Styles.ContactInfo>
      <Styles.ContactInfo data-testid="candidate-linkedinUrl">
        <Styles.ContactLink
          isDisabled={!linkedinValidLink}
          href={linkedinValidLink}
          target="_blank"
          rel="noreferrer"
        >
          <LinkedInIcon />
          <span>{linkedinUrl}</span>
        </Styles.ContactLink>
      </Styles.ContactInfo>
      <Styles.ContactInfo data-testid="candidate-mobile-phone">
        <PhoneIcon />
        <p>{mobilePhone || mask}</p>
      </Styles.ContactInfo>
      <Styles.ContactInfo data-testid="candidate-trio-url">
        <Styles.ContactLink
          isDisabled={!trioProfileValidLink}
          href={trioProfileValidLink}
          target="_blank"
          rel="noreferrer"
        >
          <LinkIcon />
          <span>{candidateBioUrl || mask}</span>
        </Styles.ContactLink>
      </Styles.ContactInfo>
    </Styles.CandidateContact>
  );
};

const CandidateEducationAndExpectation = ({
  englishLevel,
  salaryExpectation,
  availability,
}: CandidateResumeType): JSX.Element => {
  const mask = '---';
  const englishFormatted = (englishLevel ?? mask).replace(/_/g, ' ').toLowerCase();
  return (
    <Styles.CandidateContact fullWidth>
      <div>
        <Styles.SmallerText>English Level</Styles.SmallerText>
        <Styles.SimpleText>{englishFormatted}</Styles.SimpleText>
      </div>
      <div>
        <Styles.SmallerText>Salary Expectations</Styles.SmallerText>
        <Styles.SimpleText>{salaryExpectation || mask}</Styles.SimpleText>
      </div>
      <div>
        <Styles.SmallerText>Availability</Styles.SmallerText>
        <Styles.SimpleText>{availability ?? mask}</Styles.SimpleText>
      </div>
    </Styles.CandidateContact>
  );
};

const CandidateResume = function ({
  candidate,
  isNotesPage,
  openDeleteDialog,
  refetchCandidate,
}: CandidateResumeProps): JSX.Element {
  return (
    <Styles.Box>
      {candidate ? (
        <>
          <CandidateNameStatusAndPosition refetchCandidate={refetchCandidate} {...candidate} />
          {isNotesPage && <CandidateEducationAndExpectation {...candidate} />}
          {!isNotesPage && <CandidateContactInfo {...candidate} />}
          <CandidateCreationDate candidate={candidate} openDeleteDialog={openDeleteDialog} />
        </>
      ) : (
        <div className="tw-flex tw-items-center">
          <SkeletonCircle w={12} h={12} className="tw-mr-3" />
          <div>
            <Skeleton className="tw-h-4 tw-mb-1.5 tw-w-[100px]" />
            <div className="tw-flex tw-gap-3 tw-items-center">
              <Skeleton className="tw-w-10 tw-h-[18px]" />
              <Skeleton className="tw-w-[100px] tw-h-3.5" />
            </div>
          </div>
        </div>
      )}
    </Styles.Box>
  );
};

export default CandidateResume;
